import { useState, useCallback, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { IntakeData } from "@/lib/types";
import { ArrowLeft, Camera, CheckCircle, Upload } from "lucide-react";
import { ModernCamera } from "@/components/ui/modern-camera";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react";
import { msg } from "@lingui/core/macro";
 

import type {
  BatchUploadResult,
  QueuedPhotoMetadata,
  UploadProgress as UploadProgressType,
} from "@/lib/batchUploadService";

import {
  BatchUploadService,
} from "@/lib/batchUploadService";

import { UploadProgress } from "@/components/ui/upload-progress";
interface SinglePhotoRetakeProps {
  data: IntakeData;
  photoId: string;
  photoTitle: string;
  photoExample?: string;
  photoInstruction?: string;
  onUpdate: (data: Partial<IntakeData>) => void;
  onComplete: () => void;
  onCancel: () => void;
  existingMediaId?: string;
}

// Photo configurations - 6 photos only
// Translations are handled at usage time using msg macro
const getPhotoConfigs = () => ({
  upper_occlusal: {
    title: msg`Upper Occlusal`,
    instruction: msg`Open your mouth wide and tilt your head slightly up so we can see your upper teeth.`,
    example:
      "https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F6b025284885f47fc936bf371991d2694?format=webp&width=800",
  },
  frontal_retracted_apart: {
    title: msg`Frontal Retracted Apart`,
    instruction: msg`Pull your lips back with your fingers and keep your teeth slightly apart.`,
    example:
      "https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F2abffd7480e74b9c92484c3ecf5335c7?format=webp&width=800",
  },
  lower_occlusal: {
    title: msg`Lower Occlusal`,
    instruction: msg`Open your mouth wide and tilt your head down so we can see your lower teeth.`,
    example:
      "https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F1966745da916429eab37092509273047?format=webp&width=800",
  },
  right_buccal: {
    title: msg`Right Buccal`,
    instruction: msg`Turn your head to the right, pull back your cheek, and bite down gently.`,
    example:
      "https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F03c7bf08876f4869a469602542118d7f?format=webp&width=800",
  },
  frontal_in_occlusion: {
    title: msg`Frontal In Occlusion`,
    instruction: msg`Face the camera, pull your lips back, and bite down with teeth together.`,
    example:
      "https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F2fb2c38e3a0f4649abc5fab535c553ce?format=webp&width=800",
  },
  left_buccal: {
    title: msg`Left Buccal`,
    instruction: msg`Turn your head to the left, pull back your cheek, and bite down gently.`,
    example:
      "https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F98a875b6214443c6b708dcbe6413cdf6?format=webp&width=800",
  },
});

export default function SinglePhotoRetake({
  data,
  photoId,
  photoTitle,
  photoExample,
  photoInstruction,
  onUpdate,
  onComplete,
  onCancel,
  existingMediaId,
}: SinglePhotoRetakeProps) {
  const { i18n } = useLingui();
  // Removed unused isCapturing state
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] =
    useState<UploadProgressType | null>(null);
  const [showUploadProgress, setShowUploadProgress] = useState(false);
  const batchUploadService = useRef<BatchUploadService | null>(null);

  // Initialize batch upload service for retake
  useEffect(() => {
    batchUploadService.current = new BatchUploadService((progress) => {
      setUploadProgress(progress);
    });

    // Cleanup on unmount
    return () => {
      if (batchUploadService.current) {
        batchUploadService.current.cleanup();
      }
    };
  }, []);

  // Get photo config or use provided props
  const PHOTO_CONFIGS = getPhotoConfigs();
  const config = PHOTO_CONFIGS[photoId as keyof typeof PHOTO_CONFIGS];
  const title = photoTitle || (config?.title ? i18n._(config.title) : photoId);
  const instruction =
    photoInstruction ||
    (config?.instruction ? i18n._(config.instruction) : i18n._(msg`Position yourself for the photo`));
  const example = photoExample || config?.example;

  const handlePhotoCapture = useCallback(
    (imageData: string) => {
      if (!batchUploadService.current) {
        console.error("Batch upload service not available");
        return;
      }

      setCapturedPhoto(imageData);
      // Photo capture completed

      // Store photo locally for immediate preview using batch service
      const metadata: QueuedPhotoMetadata = {
        photoPosition: photoId,
        photoSequence: undefined,
        originalFilename: `${photoId}.jpg`,
        replaceMediaId: existingMediaId,
      };

      const localUrl = batchUploadService.current.addPhoto(
        photoId,
        imageData,
        metadata,
      );

      // Optimistically update with local URL so user sees the preview immediately
      const localUpdatedPhotos = {
        ...data.photos,
        [photoId]: localUrl,
      };

      onUpdate({
        photos: localUpdatedPhotos,
        [`${photoId}_timestamp`]: Date.now(),
      });

      // Begin optimized upload flow for this single photo
      (async () => {
        try {
          setIsUploading(true);
          setShowUploadProgress(true);

          console.log(
            `🔄 Starting retake upload for ${photoId} using batch service...`,
          );

          // Use batch service for consistent upload handling
          const result: BatchUploadResult =
            await batchUploadService.current!.uploadAllPhotos(
              data.email,
              data.formUid,
            );

          if (result.success) {
            console.log(`✅ ${photoId} retake upload successful!`);

            const finalUrl = result.uploadedPhotos[photoId] ?? localUrl;
            const finalPhotos = {
              ...data.photos,
              [photoId]: finalUrl,
            } as IntakeData["photos"];

            const mediaMap = {
              ...(data.photoMediaMap ?? {}),
            };

            if (result.mediaIds[photoId]) {
              mediaMap[photoId] = result.mediaIds[photoId];
            }

            onUpdate({
              photos: finalPhotos,
              photoMediaMap: mediaMap,
            });

            setTimeout(() => {
              setShowUploadProgress(false);
              onComplete();
            }, 1500);
          } else {
            console.warn(
              `⚠️ ${photoId} retake upload had issues:`,
              result.failedPhotos,
            );

            setTimeout(() => {
              setShowUploadProgress(false);
              onComplete();
            }, 2000);
          }
        } catch (error) {
          console.error("Single retake upload error:", error);

          // Still complete the flow with local photo
          setTimeout(() => {
            setShowUploadProgress(false);
            onComplete();
          }, 1500);
        } finally {
          setIsUploading(false);
        }
      })();
    },
    [
      data.photos,
      data.photoMediaMap,
      data.email,
      data.formUid,
      photoId,
      onUpdate,
      onComplete,
      batchUploadService,
      existingMediaId,
    ],
  );

  const handleRetake = useCallback(() => {
    setCapturedPhoto(null);
    // Reset photo capture state
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 lg:bg-white">
      <div className="lg:flex lg:min-h-screen">
        {/* Left Sidebar - Same as main app */}
        <div className="hidden lg:flex lg:w-80 xl:w-96 bg-gradient-to-br from-[#166B93] via-[#166B93] to-[#CEF12F] flex-col justify-between p-8 xl:p-12">
          <div>
            {/* Logo */}
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 xl:w-16 xl:h-16 bg-white rounded-full flex items-center justify-center">
                <img
                  src="https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F8b68da76c613449ca9127d9a23beba22?format=webp&width=800"
                  alt="Jema Services Tooth Icon"
                  className="w-8 h-8 xl:w-12 xl:h-12 object-contain"
                />
              </div>
              <img
                src="https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F0ab7c468120045fe80296ebb014149e8?format=webp&width=800"
                alt="Jema Services Logo"
                className="h-10 xl:h-12 object-contain brightness-0 invert"
              />
            </div>

            {/* Retake Info */}
            <div className="text-white mb-12">
              <h1 className="text-2xl xl:text-3xl font-bold mb-2">
                <Trans>Retake Photo</Trans>
              </h1>
              <p className="text-lg xl:text-xl text-white/90 mb-6">{title}</p>
              <p className="text-sm xl:text-base text-white/80 leading-relaxed">
                <Trans>Take a new photo for this position. You'll return to the review page once complete.</Trans>
              </p>
            </div>
          </div>

          {/* Bottom Branding */}
          <div className="text-white/60 text-xs">
            <p>© 2024 Jema Services</p>
            <p><Trans>Transforming smiles worldwide</Trans></p>
          </div>
        </div>

        {/* Right Content Area */}
        <div className="flex-1 lg:flex lg:flex-col">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-4 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onCancel}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <Trans>Back to Review</Trans>
                </Button>
                <div className="flex items-center gap-3">
                  <Camera className="w-6 h-6 text-[#166B93]" />
                  <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">
                    <Trans>Retake: {title}</Trans>
                  </h1>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-4 lg:p-8">
            {capturedPhoto ? (
              /* Success State */
              <div className="max-w-2xl mx-auto text-center space-y-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    <Trans>Photo Captured!</Trans>
                  </h2>
                  <p className="text-gray-600">
                    {isUploading
                      ? <Trans>Uploading your new photo...</Trans>
                      : <Trans>Your new photo has been saved. Returning to review...</Trans>}
                  </p>
                </div>

                <div className="max-w-md mx-auto">
                  <div className="aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden">
                    <img
                      src={capturedPhoto}
                      alt={title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {!isUploading && (
                  <div className="flex gap-3 justify-center">
                    <Button onClick={handleRetake} variant="outline">
                      <Trans>Retake Again</Trans>
                    </Button>
                    <Button onClick={onComplete} variant="jema">
                      <Trans>Continue to Review</Trans>
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              /* Camera State */
              <div className="max-w-6xl mx-auto">
                <div className="grid lg:grid-cols-2 gap-8">
                  {/* Camera Section */}
                  <div className="space-y-4">
                    <ModernCamera
                      onCapture={handlePhotoCapture}
                      overlayGuide={{
                        width: "12rem",
                        height: "14rem",
                        shape: "oval",
                      }}
                      instructions={{
                        title: title,
                        description: instruction,
                        example: example,
                      }}
                      autoCapture={false}
                      captureDelay={3}
                    />
                  </div>

                  {/* Instructions Section */}
                  <div className="space-y-4">
                    <Card>
                      <CardContent className="p-6">
                        <div className="space-y-4">
                          <div>
                            <h3 className="font-semibold text-xl mb-2">
                              {title}
                            </h3>
                            <p className="text-gray-600">{instruction}</p>
                          </div>

                          {example && (
                            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                              <img
                                src={example}
                                alt={`Example: ${title}`}
                                className="w-20 h-20 object-cover rounded-lg border-2 border-gray-200"
                              />
                              <div>
                                <p className="font-medium text-gray-900">
                                  <Trans>Example Position</Trans>
                                </p>
                                <p className="text-sm text-gray-600">
                                  <Trans>Position yourself like this example</Trans>
                                </p>
                              </div>
                            </div>
                          )}

                          <div className="bg-blue-50 rounded-lg p-4">
                            <h4 className="font-medium text-blue-900 mb-2">
                              <Trans>Tips for a Great Photo:</Trans>
                            </h4>
                            <ul className="text-sm text-blue-800 space-y-1">
                              <li><Trans>• Ensure good lighting on your face</Trans></li>
                              <li><Trans>• Keep the camera steady</Trans></li>
                              <li><Trans>• Follow the example position closely</Trans></li>
                              <li>
                                <Trans>• Make sure your teeth/mouth are clearly visible</Trans>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Progress Modal */}
      {showUploadProgress && uploadProgress && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Upload className="w-6 h-6 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  <Trans>Uploading Retaken Photo</Trans>
                </h3>
              </div>

              <UploadProgress
                current={uploadProgress.current}
                total={uploadProgress.total}
                currentPhoto={uploadProgress.currentPhoto}
                status={uploadProgress.status}
                uploadMode="sequential"
                className="border-0 shadow-none"
              />

              <div className="mt-4 text-center text-sm text-gray-600">
                <Trans>Your retaken photo is being uploaded with the same quality and reliability as the batch system.</Trans>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
